import React from 'react'
import { AlertTriangle, InfoIcon, LoaderCircle } from 'lucide-react'

import { Button } from '../ui/button'

import { ConfirmDialogModal, ModalRenderArgs } from '@/state/modals'

const DEFAULT_CANCEL = 'Annuler'
const DEFAULT_CONFIRM = 'Confirmer'

export const Config = {
  title: 'Confirmation',
  dismissible: true,
}

const ConfirmDialogContent = ({ modal, slots, ctx }: ModalRenderArgs<ConfirmDialogModal>) => {
  const {
    title,
    description,
    confirmLabel,
    cancelLabel,
    intent = 'default',
    icon,
    onConfirm,
    onCancel,
    resolve,
  } = modal
  const { Header, Title, Description, Footer } = slots
  const [isLoading, setIsLoading] = React.useState(false)
  const hasResolvedRef = React.useRef(false)
  const hasMountedRef = React.useRef(false)

  const safeResolve = React.useCallback(
    (result: 'confirm' | 'cancel') => {
      if (hasResolvedRef.current) return
      hasResolvedRef.current = true
      resolve?.(result)
    },
    [resolve],
  )

  React.useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true
      return () => {}
    }

    return () => {
      if (!hasResolvedRef.current) {
        safeResolve('cancel')
      }
    }
  }, [safeResolve])

  const handleCancel = React.useCallback(async () => {
    await onCancel?.()
    safeResolve('cancel')
    ctx.resolve('cancel')
  }, [onCancel, ctx, safeResolve])

  const handleConfirm = React.useCallback(async () => {
    if (isLoading) return

    if (!onConfirm) {
      safeResolve('confirm')
      ctx.resolve('confirm')
      return
    }

    try {
      setIsLoading(true)
      await onConfirm()
      safeResolve('confirm')
      ctx.resolve('confirm')
    } finally {
      setIsLoading(false)
    }
  }, [ctx, isLoading, onConfirm, safeResolve])

  const Icon =
    icon ??
    (intent === 'danger' ? (
      <AlertTriangle className="h-5 w-5 text-destructive" />
    ) : (
      <InfoIcon className="h-5 w-5 text-muted-foreground" />
    ))

  return (
    <>
      <Header className="space-y-3">
        <Title className="flex items-center gap-2">
          {Icon}
          <span>{title ?? Config.title}</span>
        </Title>
        {description ? <Description>{description}</Description> : null}
      </Header>

      <Footer className="flex flex-row justify-end gap-2 sm:flex-row">
        <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
          {cancelLabel ?? DEFAULT_CANCEL}
        </Button>
        <Button onClick={handleConfirm} variant={intent === 'danger' ? 'destructive' : 'default'} disabled={isLoading}>
          {isLoading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : (confirmLabel ?? DEFAULT_CONFIRM)}
        </Button>
      </Footer>
    </>
  )
}

export const render = (args: ModalRenderArgs<ConfirmDialogModal>) => <ConfirmDialogContent {...args} />
